/**
 * Rendu IMAGE PAR IMAGE d'une animation pour sa vidéo (lot 7 : YouTube
 * Shorts, TikTok, Reels), piloté par le worker vidéo (Playwright) :
 *
 *   1. le worker ouvre `/capture/animation` à la taille du canevas (360 × 640,
 *      échelle 3 → 1080 × 1920) et attend `window.__jrCaptureReady` ;
 *   2. `await window.__jrCapture.load(doc)` : le document de la vidéo (version
 *      publiée + voix, ta voix ou la synthèse), validé ici ; renvoie le plan
 *      (durée, voix et leurs instants, jingles) ;
 *   3. pour chaque image : `await window.__jrCapture.seek(ms)` puis capture ;
 *      FFmpeg pose les voix aux instants du MÊME plan.
 *
 * Seule donnée lue ici : l'avatar de qui a enregistré les voix (RPC publique
 * `get_speaker_avatars`) ; sinon la page ne fait que dessiner ce qu'on lui donne. Avant la première image, toutes les images de
 * la figure et toutes les polices (KaTeX compris) sont chargées : une image
 * capturée ne dépend que de son instant.
 */

import { useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import {
  documentSpeakers,
  planVideo,
  resolveColor,
  validateJraf,
  type CompiledAnimation,
  type VideoPlan,
} from '@jeraisonne/animation';
import {
  AnimationVideo,
  type AnimationVideoHandle,
} from '@jeraisonne/ui/components/animations/animation-video';
import type { SpeakerAvatars } from '@jeraisonne/ui/components/animations/speaker-avatar';
import { fetchSpeakerAvatars } from '@/lib/animations/speakers';

export interface CapturePlan extends VideoPlan {
  canvas: { width: number; height: number };
}

export interface JrCaptureApi {
  load(doc: unknown): Promise<CapturePlan>;
  seek(ms: number): Promise<void>;
}

declare global {
  interface Window {
    __jrCapture?: JrCaptureApi;
    __jrCaptureReady?: boolean;
  }
}

const nextFrame = () => new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));

/** Instants qui montrent chaque texte au moins une fois (polices à charger). */
export function warmUpTimes(plan: VideoPlan): number[] {
  const ts = new Set<number>([0, plan.coverMs]);
  for (const s of plan.scenes) {
    ts.add(s.startMs + 1);
    for (const c of s.captions) ts.add(s.startMs + c.fromMs);
    for (const g of s.gates) {
      ts.add(s.startMs + g.openMs);
      if (g.revealMs !== null) ts.add(s.startMs + g.revealMs);
    }
    ts.add(s.startMs + s.durationMs - 1);
  }
  return [...ts].filter((t) => t >= 0 && t < plan.durationMs).sort((a, b) => a - b);
}

function loadImage(url: string): Promise<void> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve();
    img.onerror = () => resolve(); // une image manquante ne bloque pas la vidéo
    img.src = url;
  });
}

export function AnimationCapture({ assetUrl }: { assetUrl: (path: string) => string }) {
  const [loaded, setLoaded] = useState<{
    anim: CompiledAnimation;
    plan: VideoPlan;
    avatars: SpeakerAvatars;
  } | null>(null);
  const handle = useRef<AnimationVideoHandle>(null);
  const urlOf = useRef(assetUrl);
  urlOf.current = assetUrl;

  useEffect(() => {
    const api: JrCaptureApi = {
      async load(doc) {
        const r = validateJraf(doc);
        if (!r.ok)
          throw new Error(
            `Document de la vidéo invalide : ${r.errors
              .slice(0, 3)
              .map((e) => `${e.path} ${e.message}`)
              .join(' ; ')}`,
          );
        const { anim, plan } = planVideo(r.doc);
        // avatar de qui a enregistré les voix : sans lui (réseau), la vidéo se fait quand même
        const avatars = await fetchSpeakerAvatars(documentSpeakers(r.doc)).catch(
          () => ({}) as SpeakerAvatars,
        );
        flushSync(() => setLoaded({ anim, plan, avatars }));
        await Promise.all([
          ...r.doc.assets
            .filter((a) => a.kind === 'image')
            .map((a) => loadImage(urlOf.current(a.path))),
          ...Object.values(avatars).flatMap((a) => a.frames.map(loadImage)),
        ]);
        // chaque texte montré une fois : ses polices (Inter, KaTeX) se chargent
        for (const t of warmUpTimes(plan)) handle.current?.seek(t);
        await document.fonts?.ready;
        await nextFrame();
        handle.current?.seek(0);
        await nextFrame();
        return { ...plan, canvas: { width: r.doc.canvas.width, height: r.doc.canvas.height } };
      },
      async seek(ms) {
        if (!handle.current) throw new Error('Aucune animation chargée');
        handle.current.seek(ms);
        await nextFrame();
      },
    };
    window.__jrCapture = api;
    window.__jrCaptureReady = true;
    return () => {
      delete window.__jrCapture;
      window.__jrCaptureReady = false;
    };
  }, []);

  const background = loaded ? resolveColor(loaded.anim.doc.canvas.background) : '#061433';
  return (
    <div
      data-capture="animation"
      className="fixed inset-0 overflow-hidden"
      style={{ backgroundColor: background }}
    >
      {loaded && (
        <AnimationVideo
          ref={handle}
          anim={loaded.anim}
          plan={loaded.plan}
          assetUrl={assetUrl}
          speakerAvatars={loaded.avatars}
        />
      )}
    </div>
  );
}
